import React from 'react';
import {
  HighContrastSelector,
  IMessageBarProps,
  IMessageBarStyleProps,
  IMessageBarStyles,
  IStackTokens,
  IStyleFunctionOrObject,
  Layer,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  LayerHost,
  memoizeFunction,
  mergeStyleSets,
  MessageBar,
  MessageBarType,
  Stack,
  useTheme,
} from '@fluentui/react';
import {
  CheckmarkCircle24Filled as SuccessIcon,
  Dismiss16Regular as CloseIcon,
  Info24Filled as InfoIcon,
  Warning24Filled as WarningIcon,
} from '@fluentui/react-icons';
import { ActionButton, IconButton } from 'common/components/Buttons/v8';
import { layout_wrapper_id } from 'common/components/Layout/Layout';
import { Text } from 'common/components/Text';
import { IAppTheme } from 'common/theme/AppTheme';
import { focusFirstTabbable } from 'common/utilities/Accessibility';
import { useLocalizedStrings } from 'i18n';

import { getStyles } from './Snackbar.styles';

export interface ISnackBarActionProps {
  id: string;
  text: string;
  ariaLabel?: string;
  onClick: () => void;
}

export interface ISnackbarProps extends Omit<IMessageBarProps, 'actions'> {
  /**
   * If `hideIcon` is set to `true`, then no icon will be displayed
   * in the the notification.
   *
   * @default false
   */
  hideIcon?: boolean;
  /**
   * Message text for the snackbar notification.
   */
  message: string;
  /**
   * The type of notification.
   * These types determine the color and iconography.
   */
  messageType: SnackbarType;
  /**
   * Whether the message bar has a dismiss button and its callback.
   * If not provided, we don't show a dismiss button.
   *
   * @default undefined
   */
  onDismiss?: () => void;
  /**
   * Provide a unique id to a {@link LayerHost} that will wrap the snackbar
   * notification with absolute positioning.
   * Use this prop to render a snackbar notification inside a specific parent
   * container, such as a modal.
   *
   * @default undefined
   */
  layerHostId?: string;
  /**
   * Custom styles for the {@link MessageBar} wrapper.
   */
  styles?: IStyleFunctionOrObject<ISnackbarStyleProps, ISnackbarStyles>;
  /**
   * Action buttons in the snackbar message (maximum of 2).
   */
  actions?: ISnackBarActionProps[];
}

export interface ISnackbarStyleProps extends IMessageBarStyleProps {}
export interface ISnackbarStyles extends IMessageBarStyles {}

// eslint-disable-next-line max-len
// Copied from https://github.com/microsoft/fluentui/blob/d7481c4c6c6a21e97507dbca99c403577a6dd507/packages/react/src/components/MessageBar/MessageBar.types.ts#L235
export const enum SnackbarType {
  /** Info styled MessageBar */
  info = 0,
  /** Error styled MessageBar */
  error = 1,
  /** Blocked styled MessageBar */
  blocked = 2,
  /** SevereWarning styled MessageBar */
  severeWarning = 3,
  /** Success styled MessageBar */
  success = 4,
  /** Warning styled MessageBar */
  warning = 5,
}

const baseline = 8;

const size = {
  lg: baseline * 4,
  md: baseline * 2,
  sm: baseline * 1.5,
  xs: baseline,
} as const;

const textContainerTokens: IStackTokens = { childrenGap: size.sm, padding: `0 ${size.md} 0` };
const iconWrapperTokens: IStackTokens = { padding: '3px 0 0' };
const textWrapperTokens: IStackTokens = { padding: '4px 0 0' };
const dismissButtonWrapperTokens: IStackTokens = { padding: '0 0 0 4px' };
/**
 * Universal bottom spacing relative to the parent layer,
 * used for absolute positioning.
 */
export const snackbarBottomSpacing = size.lg;

export const snackbarZIndex = 1000000001;

/**
 * A styled {@link MessageBar} that will render an svg Fluent System icon
 * (rather than default MDL2 font icon) depending on {@link SnackbarType}.
 *
 * @param props ISnackbarProps
 * @returns {React.ReactElement} A styled {@link MessageBar}
 */
export const Snackbar: React.FunctionComponent<ISnackbarProps> = (props: ISnackbarProps) => {
  const { actions, message, messageType, onDismiss = undefined, hideIcon = false, layerHostId = undefined, styles } = props;
  const theme: IAppTheme = useTheme();
  const { t } = useLocalizedStrings();
  const defaultStyles = getStyles(theme);

  const config = {
    [SnackbarType.info]: {
      icon: <InfoIcon primaryFill={theme.palette.white} />,
      backgroundColor: theme.palette.neutralDark,
      type: MessageBarType.info,
    },
    [SnackbarType.blocked]: {
      icon: <WarningIcon primaryFill={theme.appColors?.redPrimaryToRed10} />,
      backgroundColor: theme.palette.neutralDark,
      type: MessageBarType.blocked,
    },
    [SnackbarType.severeWarning]: {
      icon: <WarningIcon primaryFill={theme.appColors?.redPrimaryToRed10} />,
      backgroundColor: theme.palette.neutralDark,
      type: MessageBarType.severeWarning,
    },
    [SnackbarType.success]: {
      icon: <SuccessIcon primaryFill={theme.palette.green} />,
      backgroundColor: theme.palette.neutralDark,
      type: MessageBarType.success,
    },
    [SnackbarType.error]: {
      icon: <WarningIcon primaryFill={theme.appColors?.redPrimaryToRed10} />,
      backgroundColor: theme.palette.neutralDark,
      type: MessageBarType.error,
    },
    [SnackbarType.warning]: {
      icon: <WarningIcon primaryFill={theme.appColors?.redPrimaryToRed10} />,
      backgroundColor: theme.palette.neutralDark,
      type: MessageBarType.warning,
    },
  };

  const { backgroundColor, icon, type } = config[messageType];
  const showIcon = !hideIcon && icon;

  const buttonContainerTokens: IStackTokens = { childrenGap: size.xs, padding: hideIcon ? 0 : `0 0 0 ${size.lg}px` };

  // Define default snackbar styles
  const snackbarStyles: IMessageBarStyles = {
    root: {
      background: backgroundColor,
      borderRadius: 8,
      boxShadow: theme.effects.elevation16,
      boxSizing: 'border-box',
      color: theme.palette.white,
      maxWidth: 320,
      minWidth: 240,
      paddingTop: 0,
      paddingBottom: 3,
      paddingLeft: size.sm,
      paddingRight: onDismiss ? 0 : size.md,
      minHeight: 48,
      width: 'auto',
      zIndex: snackbarZIndex + 1,

      [HighContrastSelector]: {
        backgroundColor: 'Canvas',
        color: 'CanvasText',
      },
    },
    iconContainer: {
      display: 'none',
    },
    innerText: {
      width: '100%',
    },
  };

  // Combine default snackbar styles with custom styles:
  const getSnackbarStyles = memoizeFunction((customStyles?: IStyleFunctionOrObject<ISnackbarStyleProps, ISnackbarStyles>) =>
    customStyles
      ? (styleProps: ISnackbarStyleProps) =>
          mergeStyleSets(snackbarStyles, typeof customStyles === 'object' ? customStyles : customStyles(styleProps))
      : snackbarStyles
  );

  const snackbarActions = actions ? (
    <Stack horizontal tokens={buttonContainerTokens} wrap styles={defaultStyles.actionsWrapper}>
      {actions
        // maximum of 2 actions allowed
        .slice(0, 2)
        .map((action) => (
          <Stack.Item key={action.id} disableShrink>
            <ActionButton
              styles={defaultStyles.actionButton}
              onClick={action.onClick}
              {...(action.ariaLabel && { ariaLabel: action.ariaLabel })}
            >
              {action.text}
            </ActionButton>
          </Stack.Item>
        ))}
    </Stack>
  ) : undefined;

  const handleDismiss = () => {
    focusFirstTabbable(layout_wrapper_id);
    onDismiss?.();
  };

  const closeIconElement = <CloseIcon />;

  const snackbar = (
    <MessageBar
      isMultiline={true}
      messageBarIconProps={{
        iconName: undefined,
      }}
      delayedRender={false}
      messageBarType={type}
      styles={getSnackbarStyles(styles)}
      actions={snackbarActions}
    >
      <Stack horizontal horizontalAlign="space-between">
        <Stack horizontal tokens={textContainerTokens}>
          {showIcon && (
            <Stack.Item align="start" tokens={iconWrapperTokens} disableShrink>
              {icon}
            </Stack.Item>
          )}
          <Stack.Item align="start" tokens={textWrapperTokens} styles={defaultStyles.textWrapper} title={message}>
            <Text variant="Body1Strong" styles={defaultStyles.text}>
              {message}
            </Text>
          </Stack.Item>
        </Stack>
        {onDismiss && (
          <Stack.Item align="start" tokens={dismissButtonWrapperTokens} disableShrink>
            <IconButton
              aria-label={t('CloseAlert')}
              styles={defaultStyles.dismissButton}
              onRenderIcon={() => closeIconElement}
              onClick={handleDismiss}
            />
          </Stack.Item>
        )}
      </Stack>
    </MessageBar>
  );

  if (layerHostId) {
    return <Layer hostId={layerHostId}>{snackbar}</Layer>;
  }

  return snackbar;
};
